import { Box, Flex, Link, Text } from "@chakra-ui/react"
import { navigate, withPrefix } from "gatsby"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useEffectOnce } from "react-use"
import { RemoteStoreApi } from "~/components/store/RemoteStoreTypes"
import StoreAddress from "~/components/store/StoreAddress"
import StoreBuyFromButton from "~/components/store/StoreBuyFromButton"
import StoreHours from "~/components/store/StoreHours"
import StoreMapStatic from "~/components/store/StoreMapStatic"
import { usePrinticularApi } from "~/hooks/usePrinticularApi"
import { interactionResponse } from "~/services/webvitals/defer"

interface Props {
  pageContext: {
    store: RemoteStoreApi
    appVersion: string
  }
}

const Store: React.FC<Props> = ({ pageContext }) => {
  const { store, appVersion } = pageContext

  useEffect(() => {
    window.appVersion = appVersion
  }, [appVersion])

  const { t } = useTranslation()
  const api = usePrinticularApi()

  const [, setIsLoadingStore] = useState(true)
  const [isStoreActive, setIsStoreActive] = useState(true)

  const { storeServices = [], name } = store

  const hasStoreServices = storeServices.length > 0

  useEffectOnce(() => {
    const { id: remoteStoreId, remotePrintServiceId } = store

    const fetchStore = async () => {
      const remoteStore = await api.getStoreDetails({
        remotePrintServiceId,
        remoteStoreId,
      })

      setIsStoreActive(Boolean(remoteStore.active))
      setIsLoadingStore(false)
    }

    fetchStore()
  })

  return (
    <>
      <Flex flexDir="row" alignItems="center" mb={3} mt={1}>
        <Box
          textDecoration="underline"
          _hover={{
            textDecoration: "none",
          }}
        >
          <Link
            href={withPrefix("/")}
            id="store-link-home"
            onClick={async e => {
              e.preventDefault()
              await interactionResponse()
              navigate("/")
            }}
          >
            {t("templates.layout.stores.Store.Home")}
          </Link>
        </Box>
        <Text mx={2} fontSize="xs">
          /
        </Text>
        <Box
          textDecoration="underline"
          _hover={{
            textDecoration: "none",
          }}
        >
          <Link
            href={withPrefix("/stores/")}
            id="store-link-back"
            onClick={async e => {
              e.preventDefault()
              await interactionResponse()
              navigate("/stores/")
            }}
          >
            {t("templates.layout.stores.Store.OurStores")}
          </Link>
        </Box>
        <Text mx={2} fontSize="xs">
          /
        </Text>
        <Text color="gray.400" id="store-breadcrumb">
          {name}
        </Text>
      </Flex>

      <Flex
        mt={10}
        flexDirection={{ base: "column", sm: "row" }}
        gap={6}
        mb={8}
      >
        <Flex flexDirection="row" gap={3} flex={1}>
          <Flex flexDirection="column" flex={1}>
            <Flex
              flexDirection={{ base: "column", lg: "row" }}
              alignItems="start"
              justifyContent="space-between"
              gap={{ base: 6, lg: 3 }}
            >
              <StoreAddress store={store} />
              <StoreBuyFromButton store={store} isStoreActive={isStoreActive} />
            </Flex>
            {hasStoreServices && (
              <>
                <Box as="hr" my={8} />
                <Flex
                  flexDirection={{ base: "column", lg: "row" }}
                  gap={{ base: 6, lg: 3 }}
                >
                  {storeServices.map(storeService => {
                    return (
                      <Flex key={storeService.id} flex={1}>
                        <StoreHours storeService={storeService} />
                      </Flex>
                    )
                  })}
                </Flex>
              </>
            )}
          </Flex>
        </Flex>
        <Flex flex={1} alignItems="start">
          <StoreMapStatic store={store} />
        </Flex>
      </Flex>
    </>
  )
}

export default Store
